import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  ArrowLeft,
  Edit2,
  Plus,
  Users,
  BookOpen,
  Loader2,
  AlertCircle,
  CheckCircle,
  Save,
  X,
  GitBranch,
} from 'lucide-react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  getFiliereById,
  updateFiliere,
  createSemester,
  getChefsByFiliere,
  removeChef,
  addFiliereOption,
  renameFiliereOption,
  archiveFiliereOption,
  getFiliereCampusAssignments,
} from '@/services/filieresService';
import type {
  FiliereWithHierarchy,
  FiliereChefWithUser,
  UpdateFiliereData,
  WriteGuard,
} from '@/types/filiere.types';
import SemesterCard from '../components/SemesterCard';
import AssignChefModal from '../components/AssignChefModal';
import DeleteImpactModal from '../components/filieres/DeleteImpactModal';
import { useCampusScopeWarning } from '../components/filieres/useCampusScopeWarning';

const FiliereDetailPage: React.FC = () => {
  const { filiereId } = useParams<{ filiereId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.FILIERE_MANAGE);

  // State
  const [filiere, setFiliere] = useState<FiliereWithHierarchy | null>(null);
  const [chefs, setChefs] = useState<FiliereChefWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<UpdateFiliereData>({});
  const [isSaving, setIsSaving] = useState(false);

  // Options management
  const [showAddOption, setShowAddOption] = useState(false);
  const [newOptionCode, setNewOptionCode] = useState('');
  const [newOptionName, setNewOptionName] = useState('');
  const [isSavingOption, setIsSavingOption] = useState(false);
  const [renamingOptionId, setRenamingOptionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmArchiveId, setConfirmArchiveId] = useState<string | null>(null);
  const [wantsOptions, setWantsOptions] = useState(false);

  // Semester creation
  const [isCreatingSemester, setIsCreatingSemester] = useState(false);

  // Chef modal
  const [showChefModal, setShowChefModal] = useState(false);

  // Campus scope: warn (never block) before writing to a filière of another campus
  const [filiereCampusIds, setFiliereCampusIds] = useState<string[] | undefined>(undefined);
  const { guard: guardCampusScope, modal: campusWarningModal } = useCampusScopeWarning();
  const guardWrite: WriteGuard = useCallback(
    (action) => guardCampusScope(filiereCampusIds, action),
    [guardCampusScope, filiereCampusIds],
  );

  // Wrapper functions that preserve scroll position
  const setSuccessPreserveScroll = useCallback((msg: string) => {
    const scrollY = window.scrollY;
    setSuccess(msg);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }, []);

  const setErrorPreserveScroll = useCallback((msg: string) => {
    const scrollY = window.scrollY;
    setError(msg);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }, []);

  // Load filière
  const loadFiliere = useCallback(async (showLoading = true) => {
    if (!filiereId) return;

    const scrollY = window.scrollY;

    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const [filiereData, chefsData, campusAssignments] = await Promise.all([
        getFiliereById(filiereId),
        getChefsByFiliere(filiereId),
        // Campus links only feed the advisory warning: never fail the page on them
        getFiliereCampusAssignments(filiereId).catch(() => null),
      ]);
      setFiliere(filiereData);
      setChefs(chefsData);
      setFiliereCampusIds(campusAssignments ? campusAssignments.campuses.map((c) => c.id) : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
      // Preserve scroll position when not showing loading state
      if (!showLoading) {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      }
    }
  }, [filiereId]);

  useEffect(() => {
    loadFiliere();
  }, [loadFiliere]);

  // Initialize edit data ONCE when entering edit mode (never on background
  // refreshes — re-initializing mid-edit would wipe the user's typed changes)
  const startEditing = () => {
    if (!filiere) return;
    setEditData({
      codeFiliere: filiere.codeFiliere,
      diplome: filiere.diplome,
      filiere: filiere.filiere,
      nombreAnnees: filiere.nombreAnnees,
      anneeAccreditation: filiere.anneeAccreditation,
      // Parent filières: mirror the default shown in the select so what the
      // user sees is what gets saved (stored null used to display as S5)
      specialisationStartSemester: filiere.parentCodeFiliere
        ? filiere.specialisationStartSemester
        : filiere.specialisationStartSemester ?? (filiere.nombreAnnees - 1) * 2 + 1,
    });
    setWantsOptions((filiere.options ?? []).length > 0);
    setIsEditing(true);
  };

  // Check if this filière is a parent (has options)
  const isParentFiliere = filiere && !filiere.parentCodeFiliere;

  // Calculate default specialisation start (first semester of last year)
  const getDefaultSpecialisationStart = (years: number) => (years - 1) * 2 + 1;

  // Generate semester options based on number of years
  const getSemesterOptions = (years: number) => {
    const totalSemesters = years * 2;
    return Array.from({ length: totalSemesters }, (_, i) => i + 1);
  };

  // Handle save
  const handleSave = async () => {
    if (!filiereId) return;

    setIsSaving(true);
    try {
      await updateFiliere(filiereId, editData);
      setSuccessPreserveScroll('Filière mise à jour avec succès');
      setIsEditing(false);
      loadFiliere(false);
    } catch (err) {
      setErrorPreserveScroll(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  // Options management
  const handleAddOption = async () => {
    if (!filiereId || !newOptionCode.trim() || !newOptionName.trim()) return;
    setIsSavingOption(true);
    try {
      await addFiliereOption(filiereId, newOptionCode.trim(), newOptionName.trim());
      setSuccessPreserveScroll(`Option « ${newOptionName.trim()} » ajoutée`);
      setNewOptionCode('');
      setNewOptionName('');
      setShowAddOption(false);
      loadFiliere(false);
    } catch (err) {
      setErrorPreserveScroll(err instanceof Error ? err.message : "Erreur lors de l'ajout de l'option");
    } finally {
      setIsSavingOption(false);
    }
  };

  const handleRenameOption = async (optionId: string) => {
    if (!filiereId || !renameValue.trim()) return;
    setIsSavingOption(true);
    try {
      await renameFiliereOption(filiereId, optionId, renameValue.trim());
      setSuccessPreserveScroll('Option renommée');
      setRenamingOptionId(null);
      setRenameValue('');
      loadFiliere(false);
    } catch (err) {
      setErrorPreserveScroll(err instanceof Error ? err.message : 'Erreur lors du renommage');
    } finally {
      setIsSavingOption(false);
    }
  };

  const handleArchiveOption = async (optionId: string) => {
    if (!filiereId) return;
    setIsSavingOption(true);
    try {
      await archiveFiliereOption(filiereId, optionId);
      setSuccessPreserveScroll('Option archivée');
      setConfirmArchiveId(null);
      loadFiliere(false);
    } catch (err) {
      setErrorPreserveScroll(err instanceof Error ? err.message : "Erreur lors de l'archivage");
      setConfirmArchiveId(null);
    } finally {
      setIsSavingOption(false);
    }
  };

  // Handle add semester
  const handleAddSemester = async () => {
    if (!filiereId) return;

    setIsCreatingSemester(true);
    try {
      await createSemester(filiereId);
      setSuccessPreserveScroll('Semestre créé avec succès');
      loadFiliere(false); // Don't show loading spinner to preserve scroll position
    } catch (err) {
      setErrorPreserveScroll(err instanceof Error ? err.message : 'Erreur lors de la création du semestre');
    } finally {
      setIsCreatingSemester(false);
    }
  };

  // Handle remove chef
  const handleRemoveChef = async (userId: string) => {
    if (!filiereId) return;

    try {
      await removeChef(filiereId, userId);
      setSuccessPreserveScroll('Chef de filière retiré');
      loadFiliere(false);
    } catch (err) {
      setErrorPreserveScroll(err instanceof Error ? err.message : 'Erreur lors du retrait');
    }
  };

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  // Check if can add more semesters
  // Count unique semester numbers (not total records, since option-specific semesters share the same number)
  const maxSemesters = filiere ? filiere.nombreAnnees * 2 : 0;
  const uniqueSemesterNumbers = filiere?.semesters
    ? new Set(filiere.semesters.map((s) => s.number)).size
    : 0;
  const canAddSemester = uniqueSemesterNumbers < maxSemesters;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (!filiere) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-2">Filière non trouvée</h2>
        <button
          onClick={() => navigate(-1)}
          className="text-primary-600 hover:text-primary-700"
        >
          Retour
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-primary-600" />
            {filiere.filiere}
          </h1>
          <p className="text-gray-500 mt-1">
            {filiere.codeFiliere} • {filiere.diplome} • {filiere.nombreAnnees} ans
          </p>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Filière Info Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Informations de la Filière</h2>
          {!canManage ? null : !isEditing ? (
            <button
              onClick={() => guardWrite(startEditing)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Modifier
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Enregistrer
              </button>
            </div>
          )}
        </div>

        <div className="p-6">
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isParentFiliere && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code de la Filière
                    </label>
                    <input
                      type="text"
                      value={editData.codeFiliere || ''}
                      onChange={(e) => setEditData({ ...editData, codeFiliere: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Le changement de code est répercuté automatiquement sur les options et les étudiants inscrits.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Intitulé de la Filière
                  </label>
                  <input
                    type="text"
                    value={editData.filiere || ''}
                    onChange={(e) => setEditData({ ...editData, filiere: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Diplôme
                  </label>
                  <input
                    type="text"
                    value={editData.diplome || ''}
                    onChange={(e) => setEditData({ ...editData, diplome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée (années)
                  </label>
                  <select
                    value={editData.nombreAnnees || 3}
                    onChange={(e) => {
                      const years = parseInt(e.target.value, 10);
                      const maxSem = years * 2;
                      const spec = editData.specialisationStartSemester;
                      setEditData({
                        ...editData,
                        nombreAnnees: years,
                        // Keep the specialisation semester within the new duration
                        specialisationStartSemester:
                          spec != null && spec > maxSem ? (years - 1) * 2 + 1 : spec,
                      });
                    }
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>
                        {n} an{n > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Année d'Accréditation
                  </label>
                  <input
                    type="number"
                    value={editData.anneeAccreditation || ''}
                    onChange={(e) =>
                      setEditData({ ...editData, anneeAccreditation: parseInt(e.target.value, 10) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Options toggle - only for parent filières */}
              {isParentFiliere && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={wantsOptions}
                      onChange={(e) => {
                        const hasExistingOptions = (filiere.options ?? []).length > 0;
                        if (hasExistingOptions && !e.target.checked) return; // uncheck = archive options individually
                        setWantsOptions(e.target.checked);
                        if (e.target.checked && !hasExistingOptions) {
                          guardWrite(() => setShowAddOption(true));
                        }
                      }}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Cette filière a des options (spécialisations)
                      {(filiere.options ?? []).length > 0 && (
                        <span className="ml-2 text-xs text-purple-600">
                          {(filiere.options ?? []).length} option(s) — pour les retirer, archivez-les dans la section Options
                        </span>
                      )}
                    </span>
                  </label>
                  {wantsOptions && (filiere.options ?? []).length === 0 && (
                    <p className="mt-2 ml-7 text-xs text-purple-600">
                      Ajoutez au moins une option dans la section « Options de la Filière » ci-dessous.
                    </p>
                  )}
                </div>
              )}

              {/* Specialisation Start Semester - only for parent filières with options */}
              {isParentFiliere && wantsOptions && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <GitBranch className="w-5 h-5 text-purple-600" />
                    <label className="text-sm font-medium text-purple-800">
                      Début de la spécialisation (pour les options)
                    </label>
                  </div>
                  <select
                    value={editData.specialisationStartSemester || getDefaultSpecialisationStart(editData.nombreAnnees || filiere.nombreAnnees)}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      setEditData({ ...editData, specialisationStartSemester: value });
                    }}
                    className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    {getSemesterOptions(editData.nombreAnnees || filiere.nombreAnnees).map((sem) => (
                      <option key={sem} value={sem}>
                        Semestre {sem} (S{sem})
                        {sem === getDefaultSpecialisationStart(editData.nombreAnnees || filiere.nombreAnnees) && ' - défaut'}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-purple-600">
                    Les semestres avant S{editData.specialisationStartSemester || getDefaultSpecialisationStart(editData.nombreAnnees || filiere.nombreAnnees)} seront communs à toutes les options.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Code</div>
                  <div className="font-medium text-gray-900">{filiere.codeFiliere}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Diplôme</div>
                  <div className="font-medium text-gray-900">{filiere.diplome}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Durée</div>
                  <div className="font-medium text-gray-900">{filiere.nombreAnnees} ans</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Accréditation</div>
                  <div className="font-medium text-gray-900">{filiere.anneeAccreditation}</div>
                </div>
                {isParentFiliere && (
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Options</div>
                    <div className="font-medium text-gray-900">
                      {(filiere.options ?? []).length > 0
                        ? `Oui — ${(filiere.options ?? []).length} option(s)`
                        : 'Non'}
                    </div>
                  </div>
                )}
              </div>

              {/* Show specialisation info if set */}
              {isParentFiliere && filiere.specialisationStartSemester && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                  <GitBranch className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700">
                    Spécialisation à partir du Semestre {filiere.specialisationStartSemester}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Options de la Filière (parents only) */}
      {isParentFiliere && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600" />
              <h2 className="font-semibold text-gray-900">Options de la Filière (spécialisations)</h2>
            </div>
            {canManage && !showAddOption && (
              <button
                onClick={() => guardWrite(() => setShowAddOption(true))}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Ajouter une option
              </button>
            )}
          </div>

          <div className="p-6 space-y-3">
            {showAddOption && canManage && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-purple-800 mb-1">Code de l'option</label>
                    <input
                      type="text"
                      value={newOptionCode}
                      onChange={(e) => setNewOptionCode(e.target.value.toUpperCase())}
                      placeholder={`Ex : MKT → ${filiere.codeFiliere}_MKT`}
                      className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-purple-800 mb-1">Nom de l'option</label>
                    <input
                      type="text"
                      value={newOptionName}
                      onChange={(e) => setNewOptionName(e.target.value)}
                      placeholder="Ex : Marketing Digital"
                      className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-purple-600">
                  À partir de S{editData.specialisationStartSemester || filiere.specialisationStartSemester || (filiere.nombreAnnees - 1) * 2 + 1},
                  les semestres deviennent spécifiques à chaque option. Les semestres communs vides seront convertis automatiquement.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setShowAddOption(false); setNewOptionCode(''); setNewOptionName(''); }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAddOption}
                    disabled={isSavingOption || !newOptionCode.trim() || !newOptionName.trim()}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isSavingOption ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Ajouter l'option
                  </button>
                </div>
              </div>
            )}

            {(filiere.options ?? []).length === 0 && !showAddOption ? (
              <div className="text-center py-6 text-gray-500">
                <GitBranch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>Aucune option — cette filière est un tronc commun unique</p>
                {canManage && (
                  <button
                    onClick={() => guardWrite(() => setShowAddOption(true))}
                    className="mt-2 text-purple-600 hover:text-purple-700 text-sm"
                  >
                    + Ajouter une première option
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {(filiere.options ?? []).map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xs font-mono font-medium px-2 py-1 bg-purple-100 text-purple-700 rounded">
                        {option.codeFiliere}
                      </span>
                      {renamingOptionId === option.id ? (
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameOption(option.id); }}
                          className="flex-1 px-2 py-1 text-sm border border-purple-300 rounded focus:ring-2 focus:ring-purple-500"
                          autoFocus
                        />
                      ) : (
                        <span className="font-medium text-gray-900 truncate">
                          {option.optionName || option.filiere}
                        </span>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 ml-3">
                        {renamingOptionId === option.id ? (
                          <>
                            <button
                              onClick={() => handleRenameOption(option.id)}
                              disabled={isSavingOption || !renameValue.trim()}
                              className="px-2 py-1 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded disabled:opacity-50"
                            >
                              OK
                            </button>
                            <button
                              onClick={() => { setRenamingOptionId(null); setRenameValue(''); }}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => guardWrite(() => { setRenamingOptionId(option.id); setRenameValue(option.optionName || ''); setConfirmArchiveId(null); })}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                              title="Renommer"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => guardWrite(() => { setConfirmArchiveId(option.id); setRenamingOptionId(null); })}
                              disabled={isSavingOption}
                              className="px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                              Archiver
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chefs de Filière */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-500" />
            <h2 className="font-semibold text-gray-900">Chefs de Filière Assignés</h2>
          </div>
          {canManage && (
            <button
              onClick={() => guardWrite(() => setShowChefModal(true))}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Assigner
            </button>
          )}
        </div>

        <div className="p-6">
          {chefs.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>Aucun chef de filière assigné</p>
              {canManage && (
                <button
                  onClick={() => guardWrite(() => setShowChefModal(true))}
                  className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
                >
                  + Assigner un chef de filière
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {chefs.map((chef) => (
                <div
                  key={chef.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-700 font-medium">
                        {chef.user?.firstName?.[0]}
                        {chef.user?.lastName?.[0]}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {chef.user?.firstName} {chef.user?.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{chef.user?.email}</div>
                    </div>
                  </div>
                  {canManage && (
                    <button
                      onClick={() => guardWrite(() => handleRemoveChef(chef.userId))}
                      className="text-sm text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
                    >
                      Retirer
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Semesters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-gray-500" />
            <h2 className="font-semibold text-gray-900">
              Semestres ({uniqueSemesterNumbers}/{maxSemesters})
            </h2>
          </div>
          {canAddSemester && canManage && (
            <button
              onClick={() => guardWrite(handleAddSemester)}
              disabled={isCreatingSemester}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
            >
              {isCreatingSemester ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Ajouter Semestre
            </button>
          )}
        </div>

        <div className="p-6">
          {filiere.semesters.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="mb-3">Aucun semestre créé</p>
              {canManage && (
                <button
                  onClick={() => guardWrite(handleAddSemester)}
                  disabled={isCreatingSemester}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 mx-auto disabled:opacity-50"
                >
                  {isCreatingSemester ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Créer le premier semestre
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filiere.semesters.map((semester) => (
                <SemesterCard
                  key={semester.id}
                  semester={semester}
                  canManage={canManage}
                  guardWrite={guardWrite}
                  onUpdate={() => loadFiliere(false)}
                  onError={setErrorPreserveScroll}
                  onSuccess={setSuccessPreserveScroll}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Assign Chef Modal */}
      {showChefModal && filiereId && filiere && (
        <AssignChefModal
          filiereId={filiereId}
          filiereName={filiere.filiere}
          assignedChefIds={chefs.map((c) => c.userId)}
          onClose={() => setShowChefModal(false)}
          onSuccess={() => {
            setShowChefModal(false);
            setSuccessPreserveScroll('Chef de filière assigné avec succès');
            loadFiliere(false);
          }}
          onError={setErrorPreserveScroll}
        />
      )}

      {/* Archive option: irreversible, its semesters/modules/elements are wiped */}
      <DeleteImpactModal
        isOpen={confirmArchiveId !== null}
        targets={confirmArchiveId ? [{ type: 'option', id: confirmArchiveId }] : []}
        title="Archiver cette option ?"
        itemName={(filiere.options ?? []).find((o) => o.id === confirmArchiveId)?.optionName ?? undefined}
        message="L'option sera archivée et ses semestres spécifiques supprimés, ainsi que les données rattachées ci-dessous."
        confirmLabel="Archiver définitivement"
        isLoading={isSavingOption}
        onConfirm={() => { if (confirmArchiveId) void handleArchiveOption(confirmArchiveId); }}
        onCancel={() => setConfirmArchiveId(null)}
      />

      {campusWarningModal}
    </div>
  );
};

export default FiliereDetailPage;
